import moment from "moment-timezone";

// Calendar maths for the Calendar screen. Events carry moment `start`/`end`
// parsed from the API's `human` strings, which are already in the user's
// effective timezone (not the browser's).

const DAY_MINUTES = 24 * 60;

export const startOfDay = (d) => moment(d).startOf("day");
export const startOfWeek = (d) => moment(d).startOf("isoWeek");

/** Minutes past midnight on the clock (10:00 is 600, even on DST days) */
export const wallMinutes = (m) =>
  m.hours() * 60 + m.minutes() + m.seconds() / 60;

// `week` splits all-day events into per-day copies; `upcoming` doesn't and
// overlaps it. Drop exact repeats so nothing shows twice.
export function mergeEvents(...lists) {
  const seen = new Set();
  return lists.flat().filter((e) => {
    const key = `${e.id}|${e.start.valueOf()}|${e.end.valueOf()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function uniqueById(events) {
  const seen = new Set();
  return events.filter((e) => !seen.has(e.id) && seen.add(e.id));
}

/** Does the event touch this day? */
export function onDay(e, day) {
  const start = startOfDay(day);
  const end = start.clone().add(1, "day");
  if (!e.is_all_day && !e.end.isAfter(e.start)) {
    return !e.start.isBefore(start) && e.start.isBefore(end);
  }
  return e.start.isBefore(end) && e.end.isAfter(start);
}

/**
 * Side-by-side columns for overlapping timed events on one day.
 * Events are clipped to the day. minMinutes stretches short events to the
 * height they're drawn at, so a 5 minute event doesn't hide under the next.
 * Returns [{ event, start, end, column, columns }] with start/end in minutes.
 */
export function layoutDay(events, day, { minMinutes = 0 } = {}) {
  const dayStart = startOfDay(day);
  const dayEnd = dayStart.clone().add(1, "day");
  // Wall clock, not elapsed time, so DST days line up with the hour grid
  const minutes = (m) => {
    if (m.isBefore(dayStart)) return 0;
    if (!m.isBefore(dayEnd)) return DAY_MINUTES;
    return wallMinutes(m);
  };

  const items = events
    .map((event) => {
      const start = minutes(event.start);
      const end = Math.min(
        DAY_MINUTES,
        Math.max(minutes(event.end), start + minMinutes),
      );
      return { event, start, end, column: 0, columns: 1 };
    })
    // Longer first on a tie, so it takes the left column
    .sort((a, b) => a.start - b.start || b.end - a.end);

  // Zero length events still occupy their start minute
  const until = (p) => Math.max(p.end, p.start + 0.001);
  let cluster = [];
  let clusterEnd = -1;
  const closeCluster = () => {
    const columns = Math.max(1, ...cluster.map((p) => p.column + 1));
    cluster.forEach((p) => (p.columns = columns));
    cluster = [];
  };

  for (const item of items) {
    // Back to back (end == start) doesn't overlap
    if (item.start >= clusterEnd) closeCluster();
    const taken = new Set(
      cluster.filter((p) => until(p) > item.start).map((p) => p.column),
    );
    while (taken.has(item.column)) item.column++;
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, until(item));
  }
  closeCluster();
  return items;
}

/** The event on now, or the next one later today. */
export function upNext(events, now = moment()) {
  return events
    .filter((e) => !e.is_all_day && e.my_response !== "declined")
    .filter((e) => e.end.isAfter(now) && e.start.isSame(now, "day"))
    .sort((a, b) => a.start - b.start)[0];
}

/** "now", "in 5 min", "in 1 hr 20 min" */
export function formatStartsIn(e, now = moment()) {
  const mins = Math.round(e.start.diff(now, "minutes", true));
  if (mins <= 0) return e.end.isAfter(now) ? "now" : "ended";
  if (mins < 60) return `in ${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `in ${h} hr ${m} min` : `in ${h} hr`;
}

export function formatTimeRange(e) {
  // Spans more than one day: show the end date too
  const endDay = !e.end.isSame(e.start, "day") && e.end.format("ddd D MMM");
  if (e.is_all_day) return endDay ? `All day, until ${endDay}` : "All day";
  return `${e.start.format("HH:mm")} – ${endDay ? `${endDay} ` : ""}${e.end.format("HH:mm")}`;
}

/** "Today", "Tomorrow", else "Wed 5 Nov" */
export function formatDayLabel(day, now = moment()) {
  const d = startOfDay(day);
  const today = startOfDay(now);
  if (d.isSame(today)) return "Today";
  if (d.isSame(today.clone().add(1, "day"))) return "Tomorrow";
  return d.format("ddd D MMM");
}

/** Location, minus the "Microsoft Teams Meeting" Outlook adds to Teams invites */
export const eventPlace = (e) =>
  e.location
    ?.split(";")
    .map((s) => s.trim())
    .filter((s) => s && !/^microsoft teams meeting$/i.test(s))
    .join("; ") || null;

const PROVIDER_LABEL = {
  teams: "Teams",
  zoom: "Zoom",
  meet: "Google Meet",
  webex: "Webex",
  other: "Online",
};

export const providerLabel = (p) => (p ? PROVIDER_LABEL[p] : null);

export const onlineLabel = (e) =>
  e.is_online ? providerLabel(e.online_provider) || "Online" : null;
